import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createRoot } from 'react-dom/client';
import authService from '../../services/authService';
import creditService from '../../services/creditService';

interface Section {
  id: string;
  name: string;
}

interface NavigationProps {
  sections: Section[];
  currentSection: number;
  onSectionClick: (index: number) => void;
}

interface User {
  id: string;
  provider: string;
  email: string;
  name: string;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  last_login: string | null;
}

const Navigation: React.FC<NavigationProps> = ({ sections, currentSection, onSectionClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isRechargeOpen, setIsRechargeOpen] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number>(0);
  const [canClaimDaily, setCanClaimDaily] = useState<boolean>(true);

  // Check authentication and load user data
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // isAuthenticated is now async - must await it
        const isAuth = await authService.isAuthenticated();

        if (isAuth) {
          setIsAuthenticated(true);

          // Fetch user data
          const freshUser = await authService.getCurrentUser();
          if (freshUser) {
            setUser(freshUser);
          }

          // Fetch credit balance
          const balanceData = await creditService.getBalance();
          if (balanceData) {
            setCreditBalance(balanceData.balance);
            setCanClaimDaily(balanceData.can_claim_daily);
          }
        }
      } catch (error) {
        console.error('Error checking auth:', error);
      }
    };

    checkAuth();
  }, []);

  const handleOpenRecharge = () => {
    setIsRechargeOpen(true);
  };

  const handleCloseRecharge = () => {
    setIsRechargeOpen(false);
  };

  const handleDailyClaimed = (newBalance: number) => {
    setCreditBalance(newBalance);
    setCanClaimDaily(false);
  };

  // Render UserMenu when user data is available
  useEffect(() => {
    if (user && isAuthenticated) {
      // Dynamically import and render UserMenu
      import('../../components/UserMenu').then(({ default: UserMenu }) => {
        const container = document.getElementById('landing-user-menu-root');
        if (container) {
          // Clear existing content and re-render with updated props
          container.innerHTML = '';
          const root = createRoot(container);
          root.render(React.createElement(UserMenu, {
            user,
            creditBalance,
            onOpenRecharge: handleOpenRecharge
          }));
        }
      });
    }
  }, [user, isAuthenticated, creditBalance]);

  // Render RechargeModal
  useEffect(() => {
    if (isAuthenticated) {
      import('../../components/RechargeModal').then(({ default: RechargeModal }) => {
        const container = document.getElementById('landing-recharge-modal-root');
        if (container) {
          container.innerHTML = '';
          const root = createRoot(container);
          root.render(React.createElement(RechargeModal, {
            isOpen: isRechargeOpen,
            onClose: handleCloseRecharge,
            currentBalance: creditBalance,
            canClaimDaily,
            onDailyClaimed: handleDailyClaimed
          }));
        }
      });
    }
  }, [isAuthenticated, isRechargeOpen, creditBalance, canClaimDaily]);

  return (
    <>
      {/* Fixed Header */}
      <motion.header
        className="fixed top-0 left-0 right-0 z-40 px-6 py-4"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3 group">
            {/* Favicon icon */}
            <svg viewBox="0 0 64 64" width="32" height="32">
              <rect x="2" y="2" width="60" height="60" rx="12" fill="#1F2937"/>
              <rect x="12" y="28" width="4" height="8" rx="2" fill="#10B981"/>
              <rect x="19" y="22" width="4" height="20" rx="2" fill="#10B981"/>
              <rect x="26" y="18" width="4" height="28" rx="2" fill="#10B981"/>
              <rect x="33" y="24" width="4" height="16" rx="2" fill="#10B981"/>
              <rect x="40" y="20" width="4" height="24" rx="2" fill="#10B981"/>
              <rect x="47" y="26" width="4" height="12" rx="2" fill="#10B981"/>
              <circle cx="54" cy="12" r="5" fill="#10B981"/>
              <path d="M54 9 v6 M51 12 h6" stroke="#1F2937" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {/* Wordmark */}
            <svg viewBox="0 0 200 50" height="28" fill="none">
              <defs>
                <linearGradient id="navLogoGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#00f0ff"/>
                  <stop offset="100%" stopColor="#00d4e0"/>
                </linearGradient>
                <linearGradient id="navAccentGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#ff00aa"/>
                  <stop offset="100%" stopColor="#ff66cc"/>
                </linearGradient>
              </defs>
              <text x="0" y="38" fontFamily="system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" fontSize="40" fontWeight="700" letterSpacing="-1">
                <tspan fill="url(#navLogoGradient)">NaLi</tspan><tspan fill="url(#navAccentGradient)">Vox</tspan>
              </text>
            </svg>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {sections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => onSectionClick(index)}
                className={`text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  currentSection === index ? 'text-white' : 'text-gray-500 hover:text-white'
                }`}
              >
                {section.name}
              </button>
            ))}
          </nav>

          {/* User Menu or CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <>
                {/* UserMenu will be rendered here */}
                <div id="landing-user-menu-root"></div>
                {/* RechargeModal will be rendered here */}
                <div id="landing-recharge-modal-root"></div>
                <a
                  href="/app"
                  className="px-6 py-2 rounded-full text-sm font-semibold border border-white/20 hover:bg-white hover:text-black transition-all duration-300"
                >
                  Launch App
                </a>
              </>
            ) : (
              <a
                href="/login"
                className="px-6 py-2 rounded-full text-sm font-semibold border border-white/20 hover:bg-white hover:text-black transition-all duration-300"
              >
                Get Started
              </a>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden w-8 h-8 flex flex-col justify-center items-center gap-1.5"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <motion.span
              className="w-6 h-0.5 bg-white"
              animate={isMobileMenuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
            />
            <motion.span
              className="w-6 h-0.5 bg-white"
              animate={isMobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
            />
            <motion.span
              className="w-6 h-0.5 bg-white"
              animate={isMobileMenuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
            />
          </button>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            id="mobile-menu"
            role="navigation"
            aria-label="Mobile navigation"
            className="fixed inset-0 z-30 bg-dark flex flex-col items-center justify-center gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {sections.map((section, index) => (
              <motion.button
                key={section.id}
                onClick={() => {
                  onSectionClick(index);
                  setIsMobileMenuOpen(false);
                }}
                className={`text-3xl font-display font-bold ${
                  currentSection === index ? 'text-white' : 'text-gray-500'
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {section.name}
              </motion.button>
            ))}
            {isAuthenticated ? (
              <>
                <motion.a
                  href="/app"
                  className="mt-8 btn-primary"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  Launch App
                </motion.a>
                <motion.button
                  onClick={async () => {
                    await authService.logout();
                    window.location.href = '/login';
                  }}
                  className="mt-4 px-6 py-2 rounded-full text-sm font-semibold border border-white/20 hover:bg-red-500 hover:border-red-500 transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  Log Out
                </motion.button>
              </>
            ) : (
              <motion.a
                href="/login"
                className="mt-8 btn-primary"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                Get Started
              </motion.a>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Section Dots (Desktop) */}
      <motion.nav
        className="fixed right-8 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col gap-4"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        aria-label="Section navigation"
      >
        {sections.map((section, index) => (
          <button
            key={section.id}
            onClick={() => onSectionClick(index)}
            className={`nav-dot ${currentSection === index ? 'active' : ''}`}
            title={section.name}
            aria-label={`Go to ${section.name} section`}
            aria-current={currentSection === index ? 'true' : undefined}
          />
        ))}
      </motion.nav>
    </>
  );
};

export default Navigation;
